/**
 * Asset Sidecar
 *
 * Binary payloads (raster images, PDF underlays, decal textures) arrive as
 * `data:` URL strings embedded in entity payloads. Keeping them inline makes
 * every .yap/.yate save carry the full base64 of every asset again. This
 * side-store externalizes large data URLs at save time into IndexedDB keyed
 * by a content hash, replacing them with a compact
 * `yap-asset:<hash>` token, and resolves the tokens back to inline data
 * URLs when a document is loaded. Renderers keep seeing plain data URLs —
 * the swap is invisible above this layer.
 *
 * Known limitation (documented): sidecar assets are never garbage-collected
 * per document; the store is deduplicating, so growth is bounded by the
 * number of *distinct* assets placed across sessions. Also, tokens resolve
 * only on the machine that owns the IndexedDB store — copying a .yap to a
 * different computer keeps everything except embedded rasters (the same
 * external-reference tradeoff real CAD apps make with image XREFs).
 */

const DB_NAME = 'yap-assets';
const DB_VERSION = 1;
const STORE = 'assets';
/** Only externalize payloads above this many characters (≈3 KB binary). */
export const ASSET_INLINE_LIMIT = 4_096;
/** Token prefix used inside saved documents. */
const TOKEN_PREFIX = 'yap-asset:';
const TOKEN_RE = /yap-asset:[0-9a-f]{16}/g;

interface AssetRow {
    hash: string;
    dataUrl: string;
    size: number;
    savedAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'hash' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Fast deterministic string hash (FNV-1a, 64-bit folded to 16 hex chars). */
function contentHash(text: string): string {
    let h1 = 0x811c9dc5, h2 = 0x01000193;
    for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
        h2 = Math.imul(h2 + c + i, 0x85ebca6b) >>> 0;
        h2 ^= h2 >>> 13;
    }
    return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

/** True for values that are large binary payloads worth externalizing. */
function isBigDataUrl(v: unknown): v is string {
    return typeof v === 'string' && v.startsWith('data:') && v.length > ASSET_INLINE_LIMIT;
}

function walkAndCollect(value: unknown, collected: Map<string, string>): void {
    if (Array.isArray(value)) {
        value.forEach((item) => walkAndCollect(item, collected));
        return;
    }
    if (value && typeof value === 'object') {
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            if (isBigDataUrl(v)) {
                const hash = contentHash(v);
                collected.set(hash, v);
                (value as Record<string, unknown>)[k] = `${TOKEN_PREFIX}${hash}`;
            } else if (typeof v === 'object') {
                walkAndCollect(v, collected);
            }
        }
    }
}

/**
 * Replaces every large data URL inside `envelope` (mutated in place) with a
 * sidecar token and parks the payload in IndexedDB. Returns the rewritten
 * envelope; on any storage failure the envelope is still returned with as
 * few tokens as possible (assets that failed to store stay inline).
 */
export async function externalizeAssets(envelope: unknown): Promise<unknown> {
    const collected = new Map<string, string>();
    walkAndCollect(envelope, collected);
    if (collected.size === 0) return envelope;
    try {
        const db = await openDatabase();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            const store = tx.objectStore(STORE);
            collected.forEach((dataUrl, hash) => {
                const row: AssetRow = { hash, dataUrl, size: dataUrl.length, savedAt: new Date().toISOString() };
                store.put(row);
            });
            tx.oncomplete = () => { db.close(); resolve(); };
            tx.onerror = () => { db.close(); reject(tx.error); };
        });
    } catch (error) {
        // Storage unavailable: un-tokenize is impossible post-parse, but a
        // failed write means the tokens are dangling. Bail conservatively —
        // re-inline is impossible here, so surface and let the save proceed
        // (the resolve path tolerates missing assets by leaving the token).
        console.error('[assetSidecar] Failed to store assets:', error);
    }
    return envelope;
}

/**
 * Replaces `yap-asset:<hash>` tokens inside serialized document text with
 * the stored data URLs. Unknown tokens (asset pruned/missing) are left
 * untouched so the rest of the document still loads.
 */
export async function resolveAssetRefs(text: string): Promise<string> {
    if (!text.includes(TOKEN_PREFIX)) return text;
    const hashes = new Set((text.match(TOKEN_RE) ?? []).map((t) => t.slice(TOKEN_PREFIX.length)));
    if (hashes.size === 0) return text;
    let out = text;
    for (const hash of hashes) {
        try {
            const db = await openDatabase();
            const dataUrl = await new Promise<string | null>((resolve, reject) => {
                const tx = db.transaction(STORE, 'readonly');
                const request = tx.objectStore(STORE).get(hash) as IDBRequest<AssetRow | undefined>;
                request.onsuccess = () => { db.close(); resolve(request.result?.dataUrl ?? null); };
                request.onerror = () => { db.close(); reject(request.error); };
            });
            if (dataUrl) {
                out = out.split(`${TOKEN_PREFIX}${hash}`).join(dataUrl);
            }
        } catch {
            // Missing asset: keep the token, document still opens.
        }
    }
    return out;
}
